import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import { useForm } from 'react-hook-form';
import { Grid, InputAdornment } from '@mui/material';
import { LocalPostOffice, Lock } from '@mui/icons-material';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { RootStateType } from '../../store/RootReducer';
import { AppActions } from 'src/store/AppReducer';
import { forgotPassword, signIn } from 'src/services/authService';
import { AppUser } from 'src/models/appUser';
import jwt_decode from 'jwt-decode';
import { SnackBar } from 'src/components/SnackBar';
import { object, string } from 'zod';
import validator from 'validator';
import { zodResolver } from '@hookform/resolvers/zod';

// Define schemas outside of the component to avoid circular type dependencies
const loginSchema = object({
  email: string()
    .min(1, 'Email is required')
    .email('Please enter a valid Email Address'),
  password: string()
    .min(1, 'Password is required')
    .min(8, 'Password must be more than 8 characters')
});

const resetPasswordSchema = object({
  resendEmail: string()
    .min(1, 'Email is required')
    .email('Please enter a valid Email Address')
});

type LoginFormValues = {
  email: string;
  password: string;
};
type ResetPasswordFormValues = {
  resendEmail: string;
};

interface LoginFormProps {}

export const Login: React.FC<LoginFormProps> = () => {

  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(AppActions.hideLoader());
  }, [dispatch]);

  const {
    register: registerReset,
    handleSubmit: handleSubmitReset,
    formState: { errors: errorsReset }
  } = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema) as any
  });

  const {
    register: registerSignIn,
    handleSubmit: handleSubmitSignIn,
    formState: { errors: errorsSignIn }
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema) as any
  });

  const useAppSelector: TypedUseSelectorHook<RootStateType> = useSelector;
  const { isLoading } = useAppSelector((state) => state.appReducer);
  const [isForgetPassword, selectForgetPassword] = useState(false);

  const onSubmitReset = (data: { resendEmail: string }) => {
    dispatch(AppActions.showLoader());
    dispatch(AppActions.toggleSnackBarClose());

    forgotPassword(data.resendEmail)
      .then(() => {
        dispatch(
          AppActions.toggleSnackBarOpen({
            message: 'Email has been sent successfully',
            severity: 'success'
          })
        );
        dispatch(AppActions.hideLoader());
      })
      .catch(() => {
        dispatch(
          AppActions.toggleSnackBarOpen({
            message: 'Unable to send reset email',
            severity: 'error'
          })
        );
        dispatch(AppActions.hideLoader());
      });
  };

  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const onSubmitSignIn = (data: { email: string; password: string }) => {
    if (data.email === '') {
      setEmailError('Email cannot be empty!');
    } else if (data.email !== '' && !validator.isEmail(data.email)) {
      setEmailError('Enter a valid email address!');
    }
    if (data.password === '') {
      setPasswordError('Password cannot be empty!');
    }
    if (emailError === '' && passwordError === '') {
      dispatch(AppActions.showLoader());
      dispatch(AppActions.toggleSnackBarClose());

      signIn(data.email, data.password)
        .then((res) => {
          var usr: AppUser = jwt_decode(res.data);
          localStorage.setItem('user', JSON.stringify(usr));
          localStorage.setItem('access_token', res.data);
          dispatch(AppActions.setUser(usr));
          dispatch(AppActions.hideLoader());
          navigate('/dashboard');
        })
        .catch((error) => {
          console.log(error);
          dispatch(
            AppActions.toggleSnackBarOpen({
              message: `${error?.response?.data}`,
              severity: 'error'
            })
          );
          dispatch(AppActions.hideLoader());
        });
    }
  };

  return (
    <Grid
      container
      spacing={0}
      direction="column"
      alignItems="center"
      justifyContent="center"
      style={{ minHeight: '100vh' }}
    >
      <Grid item xs={3}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            boxShadow: 3,
            width: 420,
            height: 441,
            bgcolor: 'white',
            borderRadius: 2
          }}
        >
          <Box sx={{ mt: 1 }}>
            <Grid
              container
              spacing={0}
              direction="column"
              alignItems="center"
              justifyContent="center"
              style={{ minHeight: '100vh' }}
            >
              <Grid item xs={12}>
                <img
                  width={156}
                  style={{ marginBottom: 25 }}
                  height={34}
                  src={process.env.PUBLIC_URL + '/img/Logo-Coloured.svg'}
                  alt={'logo'}
                />
              </Grid>
              {!isForgetPassword ? (
                <>
                  <div id="signin">
                    <Grid item xs={12}>
                      <Typography
                        textAlign={'center'}
                        color={'#21286C'}
                        fontWeight={'bold'}
                      >
                        <p>Enter your credentials to access your account</p>
                      </Typography>
                    </Grid>
                    <form key={1} onSubmit={handleSubmitSignIn(onSubmitSignIn)}>
                      <Grid item xs={3}>
                        <TextField
                          id="email"
                          name="email"
                          margin="normal"
                          fullWidth
                          autoComplete="new-password"
                          placeholder={'Email Address'}
                          autoFocus
                          type="text"
                          error={!!errorsSignIn['email']}
                          helperText={
                            errorsSignIn['email']
                              ? errorsSignIn['email'].message
                              : ''
                          }
                          {...registerSignIn('email')}
                          style={{ width: 302 }}
                        
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <LocalPostOffice />
                              </InputAdornment>
                            )
                          }}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <TextField
                          id="password"
                          name="password"
                          margin="normal"
                          fullWidth
                          autoComplete="new-password"
                          type="password"
                          placeholder={'Password'}
                          error={!!errorsSignIn['password']}
                          helperText={
                            errorsSignIn['password']
                              ? errorsSignIn['password'].message
                              : ''
                          }
                          {...registerSignIn('password')}
                          style={{ width: 302 }}
                         
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Lock />
                              </InputAdornment>
                            )
                          }}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <Button
                          id="btn-onSubmitSignIn"
                          type="submit"
                          fullWidth
                          variant="contained"
                          sx={{ mt: 3, mb: 2 }}
                          style={{ width: 302 }}
                          className={isLoading ? 'loading' : ''}
                        >
                          Sign In
                        </Button>
                      </Grid>
                    </form>
                    <Grid item xs={12}>
                      <Typography
                        textAlign={'center'}
                        color={'#21286C'}
                        fontWeight={'bold'}
                      >
                        Forgot your password?{' '}
                        <span
                          id="btn-selectForgetPassword"
                          onClick={() => {
                            selectForgetPassword(true);
                            dispatch(AppActions.toggleSnackBarClose());
                          }}
                          style={{ color: '#6fb0e8', cursor: 'pointer' }}
                        >
                          Reset Password
                        </span>
                      </Typography>
                    </Grid>
                  </div>
                </>
              ) : (
                <>
                  <div id="forgot-password">
                    <Grid item xs={12}>
                      <Typography
                        textAlign={'center'}
                        color={'#21286C'}
                        fontWeight={'bold'}
                      >
                        <p>Enter the email to reset the password</p>
                      </Typography>
                    </Grid>
                    <form key={2} onSubmit={handleSubmitReset(onSubmitReset)}>
                      <Grid item xs={3}>
                        <TextField
                          id="resendEmail"
                          name="resendEmail"
                          margin="normal"
                          fullWidth
                          autoComplete="email"
                          placeholder={'Email Address'}
                          type="email"
                          error={!!errorsReset['resendEmail']}
                          helperText={
                            errorsReset['resendEmail']
                              ? errorsReset['resendEmail'].message
                              : ''
                          }
                          {...registerReset('resendEmail')}
                          autoFocus
                          style={{ width: 302 }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <LocalPostOffice />
                              </InputAdornment>
                            )
                          }}
                        />
                      </Grid>

                      <Grid item xs={3}>
                        <Button
                          id="btn-onSubmitReset"
                          type="submit"
                          fullWidth
                          variant="contained"
                          sx={{ mt: 3, mb: 2 }}
                          style={{ width: 302 }}
                          className={isLoading ? 'loading' : ''}
                        >
                          {isLoading ? 'sending' : 'Send Password Reset Mail'}
                        </Button>
                      </Grid>
                    </form>
                    <Grid item xs={3}>
                      <Button
                        id="btn-selectForgetPassword"
                        fullWidth
                        variant="outlined"
                        sx={{ mt: 1, mb: 2 }}
                        style={{ width: 302 }}
                        onClick={() => {
                          selectForgetPassword(false);
                          dispatch(AppActions.toggleSnackBarClose());
                        }}
                      >
                        Back
                      </Button>
                    </Grid>
                  </div>
                </>
              )}
            </Grid>
          </Box>
        </Box>
      </Grid>
      <SnackBar />
    </Grid>
  );
};
